import type { InsuranceVerdict } from '@/hooks/useInsuranceVerdicts'
import { useMemo } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'
import { Menu, Tooltip } from '@mantine/core'
import { IconDotsVertical, IconInfoCircle, IconAlertCircle, IconHistory } from '@tabler/icons-react'
import { ViewType } from '../types'
import {
  APPOINTMENT_STATUS_OPTIONS,
  APPOINTMENT_STATUS_CHIP,
  INSURANCE_STATUS_CHIP,
  type CalendarEvent,
  type AppointmentStatusValue,
} from './scheduleCalendar.tokens'

export interface ScheduleCalendarProps {
  calendarRef: React.RefObject<FullCalendar | null>
  /** Appointment + background/free-slot events for the visible range. */
  events: CalendarEvent[]
  activeView: ViewType
  /**
   * Date the grid opens on, read once at mount (PD-1206) — FullCalendar treats
   * `initialDate`/`initialView` as init-only and ignores later changes, so
   * prev/next/today keep driving navigation through the API as before.
   */
  initialDate?: string
  isStuffMember: boolean
  selectedPractitionerCount: number
  isSendingMagicLink: boolean
  insuranceVerdictMap?: Record<string, InsuranceVerdict>
  returningPatientMap?: Record<string, boolean>
  onOpenCall: (encounterId: string) => void
  onReschedule: (appt: CalendarEvent) => void
  onReassign: (appt: CalendarEvent) => void
  onCancel: (encounterId: string) => void
  onStatusChange: (appointmentId: string, status: AppointmentStatusValue) => void
  onSendMagicLink: (encounterId: string) => void
  onDatesSet: (arg: { start: Date; end: Date; view: { type: string; currentStart: Date } }) => void
  onSlotClick: (arg: DateClickArg) => void
}

/**
 * FullCalendar-based Week/Day/Month grid, isolated from SchedulePage.
 * Appointments in the same day + 30-min window are grouped into one synthetic
 * event and laid out as a 1–2 row card grid (see eventContent).
 */
export const ScheduleCalendar = ({
  calendarRef,
  events,
  activeView,
  initialDate,
  isStuffMember,
  selectedPractitionerCount,
  isSendingMagicLink,
  insuranceVerdictMap,
  returningPatientMap,
  onOpenCall,
  onReschedule,
  onReassign,
  onCancel,
  onStatusChange,
  onSendMagicLink,
  onDatesSet,
  onSlotClick,
}: ScheduleCalendarProps) => {
  // Group every appointment that starts in the same day + 30-min slot into a
  // single synthetic event so its content can lay the cards out in a grid.
  // Background/free-slot events pass through untouched.
  const slotEvents = useMemo(() => {
    const background = events.filter((e) => e.display === 'background')
    const appts = events.filter((e) => e.display !== 'background')
    const buckets = new Map<string, CalendarEvent[]>()
    for (const appt of appts) {
      if (!appt.start) continue
      const bucketStart = new Date(appt.start)
      bucketStart.setMinutes(bucketStart.getMinutes() < 30 ? 0 : 30, 0, 0)
      const key = bucketStart.toISOString()
      const list = buckets.get(key)
      if (list) list.push(appt)
      else buckets.set(key, [appt])
    }
    const grouped = Array.from(buckets.entries()).map(([key, list]) => {
      const end = new Date(key)
      end.setMinutes(end.getMinutes() + 30)
      const sorted = [...list].sort(
        (a, b) => a.start.localeCompare(b.start) || a.title.localeCompare(b.title),
      )
      return {
        id: `slot-${key}`,
        start: key,
        end: end.toISOString(),
        extendedProps: { appts: sorted },
      }
    })
    return [...background, ...grouped]
  }, [events])

  // Single appointment card, rendered one-or-more per slot inside the grid.
  const renderApptCard = (appt: CalendarEvent) => {
    const preventSlotInteraction = (
      e: React.MouseEvent<SVGSVGElement | HTMLButtonElement | HTMLDivElement, MouseEvent>,
    ) => {
      e.preventDefault()
      e.stopPropagation()
    }
    const doctorColor = appt.backgroundColor || '#316dab'
    const endIso = appt.end ? new Date(appt.end).toISOString() : ''
    const isPast = endIso ? endIso < new Date().toISOString() : false
    const statusChip = APPOINTMENT_STATUS_CHIP[appt.appointmentStatus as string]
    const verdict = insuranceVerdictMap?.[appt.id]
    const decisionChip = verdict ? INSURANCE_STATUS_CHIP[verdict.insuranceStatus] : undefined
    const timeText = appt.start
      ? new Date(appt.start)
          .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
          .toLowerCase()
      : ''
    return (
      <div
        onClick={() => onOpenCall(appt.id)}
        className="flex h-full min-w-0 cursor-pointer flex-col justify-center gap-1 overflow-hidden rounded-lg border border-[#E7E9EE] bg-white px-[9px] py-1 text-[#1F2733] shadow-[0_1px_2px_rgba(16,30,60,.10)]"
        style={{ borderLeft: `4px solid ${doctorColor}`, opacity: isPast ? 0.62 : 1 }}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          <Menu>
            <Menu.Target>
              <IconDotsVertical
                color="#9AA0A8"
                size={14}
                className="shrink-0 cursor-pointer"
                onClick={preventSlotInteraction}
              />
            </Menu.Target>
            <Menu.Dropdown>
              {isPast && (
                <Menu.Label className="flex items-center text-[10px]">
                  <IconAlertCircle color="red" size={14} className="mr-[3px]" />
                  This appointment is in the past.
                </Menu.Label>
              )}
              {appt.appointmentId && (
                <>
                  {selectedPractitionerCount > 1 && (
                    <Menu.Label className="flex items-center text-[10px]">
                      <IconInfoCircle color="orange" size={14} className="mr-[3px]" />
                      Select a single doctor to reschedule
                    </Menu.Label>
                  )}
                  <Menu.Item
                    disabled={isPast || selectedPractitionerCount > 1}
                    onClick={(e) => {
                      preventSlotInteraction(e)
                      onReschedule(appt)
                    }}
                  >
                    Reschedule
                  </Menu.Item>
                </>
              )}

              {isStuffMember && (
                <Menu.Item
                  disabled={isPast || !appt.appointmentId}
                  onClick={(e) => {
                    preventSlotInteraction(e)
                    onReassign(appt)
                  }}
                >
                  Reassign
                </Menu.Item>
              )}

              <Menu.Item
                disabled={isPast}
                onClick={(e) => {
                  preventSlotInteraction(e)
                  onCancel(appt.id)
                }}
              >
                Cancel appointment
              </Menu.Item>
              <Menu.Item
                disabled={isPast || isSendingMagicLink}
                onClick={(e) => {
                  preventSlotInteraction(e)
                  onSendMagicLink(appt.id)
                }}
              >
                Resend appointment link
              </Menu.Item>
              <Menu.Sub>
                <Menu.Sub.Target>
                  <Menu.Sub.Item>Update status</Menu.Sub.Item>
                </Menu.Sub.Target>
                <Menu.Sub.Dropdown>
                  {APPOINTMENT_STATUS_OPTIONS.map(({ value, label }) => (
                    <Menu.Item
                      key={value}
                      disabled={!appt.appointmentId}
                      fw={appt.appointmentStatus === value ? 700 : undefined}
                      onClick={(e) => {
                        preventSlotInteraction(e)
                        onStatusChange(appt.appointmentId as string, value)
                      }}
                    >
                      {label}
                    </Menu.Item>
                  ))}
                </Menu.Sub.Dropdown>
              </Menu.Sub>
            </Menu.Dropdown>
          </Menu>
          <span title={appt.title} className="min-w-0 flex-1 truncate text-[13px] font-bold">
            {appt.title}
          </span>
          {returningPatientMap?.[appt.id] && (
            <Tooltip label="Returning patient" withArrow>
              <IconHistory size={12} className="shrink-0 text-[#9AA0A8]" />
            </Tooltip>
          )}
          <span className="shrink-0 text-[11px] font-semibold text-[#6B7280] lowercase">
            {timeText}
          </span>
        </div>
        {(statusChip || decisionChip) && (
          <div className="flex min-w-0 items-center gap-1">
            {statusChip && (
              <span
                className="overflow-hidden rounded-[5px] px-[7px] py-[2px] text-[10.5px] leading-[1.4] font-bold text-ellipsis whitespace-nowrap"
                style={{ background: `${statusChip.color}1A`, color: statusChip.color }}
              >
                {statusChip.label}
              </span>
            )}
            {decisionChip && (
              <span
                className="overflow-hidden rounded-[5px] px-[7px] py-[2px] text-[10.5px] leading-[1.4] font-bold text-ellipsis whitespace-nowrap"
                style={{ background: `${decisionChip.color}14`, color: decisionChip.color }}
              >
                {decisionChip.label}
              </span>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="scheduler-container">
      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
        ref={calendarRef}
        initialView={activeView}
        initialDate={initialDate}
        headerToolbar={false}
        allDaySlot={false}
        firstDay={1}
        weekends={false}
        selectConstraint={{
          start: new Date().toISOString(),
        }}
        slotLabelContent={(arg) => {
          const time = arg.date.toLocaleTimeString([], { hour: 'numeric', hour12: true })
          return {
            html: `<span class="text-[12px] font-bold text-[#5A616B]">${String(time).toLocaleUpperCase()}</span>`,
          }
        }}
        events={slotEvents}
        eventTimeFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
        displayEventEnd={false}
        eventContent={(arg) => {
          const { event } = arg
          if (event.extendedProps.type === 'available_slot') {
            return null
          }
          const appts = event.extendedProps.appts as CalendarEvent[] | undefined
          if (!appts || appts.length === 0) {
            return null
          }
          const CAP = 4
          const hasOverflow = appts.length > CAP
          const shown = hasOverflow ? appts.slice(0, CAP - 1) : appts
          const hidden = hasOverflow ? appts.slice(CAP - 1) : []
          const cellCount = shown.length + (hasOverflow ? 1 : 0)
          const stop = (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
          }
          return (
            <div
              className="grid h-full gap-1.5"
              style={{ gridTemplateColumns: '1fr 1fr', gridAutoRows: '1fr' }}
            >
              {shown.map((appt, i) => {
                // Odd last card (and a lone card) spans the full row width.
                const isOnly = cellCount === 1
                const isLastOdd = !hasOverflow && i === shown.length - 1 && shown.length % 2 === 1
                return (
                  <div
                    key={appt.id}
                    className="min-w-0"
                    style={{ gridColumn: isOnly || isLastOdd ? '1 / -1' : 'auto' }}
                  >
                    {renderApptCard(appt)}
                  </div>
                )
              })}
              {hasOverflow && (
                <div className="min-w-0">
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <div
                        onClick={stop}
                        className="flex h-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-[#BFC6D0] bg-[#EDEFF3] text-[12px] font-bold text-[#5A616B]"
                      >
                        +{hidden.length}
                      </div>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>Also in this slot</Menu.Label>
                      {hidden.map((a) => (
                        <Menu.Item
                          key={a.id}
                          onClick={(e) => {
                            stop(e)
                            onOpenCall(a.id)
                          }}
                        >
                          {a.title}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                </div>
              )}
            </div>
          )
        }}
        selectable
        nowIndicator
        dayHeaderClassNames={() => 'border-0'}
        dayCellClassNames={() => 'border-1'}
        stickyHeaderDates={true}
        height={'75vh'}
        dayCellContent={(arg) => {
          const isToday = arg.isToday
          if (activeView !== 'dayGridMonth') {
            return null
          }
          const dayNum = arg.dayNumberText
          return {
            html: `
            <div class="width-full flex flex-col items-center justify-center h-full">
              <div class="mx-auto mb-1 flex h-6 w-6 items-center justify-center rounded-full
              ${isToday ? 'bg-[#2D74E0] text-white font-bold' : ''} text-xs">
                   ${dayNum}
              </div>
            </div>
            `,
          }
        }}
        dayHeaderContent={(arg) => {
          const isToday = arg.isToday

          if (activeView === 'dayGridMonth') {
            return {
              html: `
              <div class="flex justify-center py-1">
                <span class="text-[13px] font-semibold tracking-[.3px] ${isToday ? 'text-[#2D74E0]' : 'text-[#8A8F98]'}">${arg.text}</span>
              </div>
            `,
            }
          }

          const weekday = arg.date.toLocaleDateString(undefined, { weekday: 'short' })
          const dayNum = arg.date.getDate()
          const boxClasses = isToday
            ? 'inline-flex flex-col items-center gap-0.5 rounded-[14px] bg-[#EAF1FE] px-[22px] pt-[9px] pb-[11px]'
            : 'inline-flex flex-col items-center gap-0.5'
          return {
            html: `
            <div class="flex justify-center">
              <div class="${boxClasses}">
                <span class="text-[13px] font-semibold tracking-[.3px] ${isToday ? 'text-[#2D74E0]' : 'text-[#8A8F98]'}">${weekday}</span>
                <span class="text-[22px] font-bold ${isToday ? 'text-[#2D74E0]' : 'text-[#1F2733]'}">${dayNum}</span>
              </div>
            </div>
          `,
          }
        }}
        slotMinTime="06:00:00" // workday start
        slotMaxTime="19:00:00" // workday end
        datesSet={onDatesSet}
        dateClick={onSlotClick}
      />
    </div>
  )
}
